import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Flame, TrendingUp, Trophy, Target, Star, Users, 
  ChevronRight, Zap, Award, Gift, ArrowUpRight, Clock,
  Sparkles, Shield, Crown, Check, ExternalLink, ArrowRight,
  Rocket, Timer, Repeat
} from "lucide-react";
import { useApp } from "../contexts/AppContext";
import { DEMO } from "../constants/mockData";
import { BURNZ_LOGO } from "../constants";
import { BurnTracker } from "../components/features/BurnTracker";

// Simple Quest Card for Dashboard
const QuestCard = ({ quest }) => (
  <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-all">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-[var(--burnz-purple)]/20 flex items-center justify-center">
        <Target className="w-5 h-5 text-[var(--burnz-purple)]" />
      </div>
      <div>
        <p className="text-white font-medium text-sm">{quest.title}</p>
        <p className="text-white/40 text-xs">{quest.xp_reward} XP</p>
      </div>
    </div>
    <Button size="sm" className="bg-[var(--burnz-purple)]/20 hover:bg-[var(--burnz-purple)]/30 text-[var(--burnz-purple)] text-xs h-7">
      Start
    </Button>
  </div>
);

// Simple FireBoard for Dashboard Leaderboard
const FireBoard = ({ users = [] }) => (
  <div className="space-y-2">
    {(users || []).slice(0, 5).map((user, i) => (
      <div key={user.id || i} className="flex items-center justify-between p-2 rounded-lg bg-white/5">
        <div className="flex items-center gap-3">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-[var(--burnz-gold)] text-black' : i === 1 ? 'bg-gray-400 text-black' : i === 2 ? 'bg-[var(--burnz-orange)] text-white' : 'bg-white/10 text-white/50'}`}>
            {i + 1}
          </span>
          <Avatar className="w-8 h-8">
            <AvatarImage src={user.avatar} />
            <AvatarFallback className="bg-[var(--burnz-purple)]/20 text-[var(--burnz-purple)] text-xs">
              {user.username?.substring(0, 2).toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <p className="text-white text-sm font-medium">{user.username || 'Anonymous'}</p>
        </div>
        <p className="text-[var(--burnz-orange)] font-bold text-sm">{(user.xp || 0).toLocaleString()} XP</p>
      </div>
    ))}
  </div>
);

const DashboardPage = () => {
  const { user } = useApp();
  const [activeTab, setActiveTab] = useState("All");
  const tabs = ["All", "Daily", "Social", "Action"];
  
  // Get the onboarding quest
  const onboardingQuest = DEMO.quests.find(q => q.is_onboarding);
  const completedOnboardingTasks = onboardingQuest?.tasks.filter(t => t.is_completed).length || 0;
  const totalOnboardingTasks = onboardingQuest?.tasks.length || 0;
  const onboardingProgress = (completedOnboardingTasks / totalOnboardingTasks) * 100;

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
      <div className="flex-1 space-y-4 lg:space-y-6">
        {/* Onboarding Quest Banner - Show if not complete */}
        {onboardingProgress < 100 && (
          <Card className="bg-gradient-to-r from-[var(--burnz-orange)]/20 via-[var(--burnz-purple)]/20 to-[var(--burnz-cyan)]/20 border-[var(--burnz-orange)]/30 overflow-hidden">
            <CardContent className="p-4 lg:p-6">
              <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Badge className="bg-[var(--burnz-orange)] text-white border-0 text-xs">🔥 GET STARTED</Badge>
                    <Badge className="bg-white/10 text-white/70 border-0 text-xs">{completedOnboardingTasks}/{totalOnboardingTasks} Tasks</Badge>
                  </div>
                  <h2 className="text-base lg:text-xl font-bold text-white mb-1">Complete Your BURNZ Profile</h2>
                  <p className="text-white/60 text-xs lg:text-sm mb-3 lg:mb-4">
                    Finish setting up to earn <span className="text-[var(--burnz-orange)] font-semibold">500 XP</span> + <span className="text-[var(--burnz-purple)] font-semibold">50 BURNZ</span>
                  </p>
                  
                  {/* Progress Bar */}
                  <div className="mb-3 lg:mb-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-white/50">Progress</span>
                      <span className="text-[var(--burnz-orange)] font-medium">{Math.round(onboardingProgress)}%</span>
                    </div>
                    <div className="h-1.5 lg:h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[var(--burnz-orange)] to-[var(--burnz-purple)] rounded-full transition-all" 
                        style={{ width: `${onboardingProgress}%` }} 
                      />
                    </div>
                  </div>

                  {/* Quick Tasks - Stack on mobile */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                    {onboardingQuest?.tasks.slice(0, 3).map((task, index) => {
                      // Define engaging icons and colors per task
                      const taskStyles = [
                        { icon: '🚀', gradient: 'from-orange-500 to-red-500', label: 'BONUS 5000 XP' },
                        { icon: '🔗', gradient: 'from-purple-500 to-pink-500', label: 'CONNECT' },
                        { icon: '💬', gradient: 'from-cyan-500 to-blue-500', label: 'JOIN' }
                      ];
                      const style = taskStyles[index] || taskStyles[0];
                      
                      // For UEX task, always provide an action URL
                      const actionUrl = task.action_url || (task.title?.toLowerCase().includes('account') ? 'https://uex.us/?ref=5jcsh81dhaos' : null);
                      const displayXP = task.title?.toLowerCase().includes('account') ? 5000 : task.xp_reward;
                      
                      return (
                        <div 
                          key={task.id} 
                          className={`relative flex flex-col p-4 rounded-2xl transition-all transform hover:scale-[1.02] ${
                            task.is_completed 
                              ? 'bg-[var(--burnz-green)]/10 border-2 border-[var(--burnz-green)]/30' 
                              : `bg-gradient-to-br ${style.gradient}/10 border-2 border-white/10 hover:border-white/30`
                          }`}
                        >
                          {/* Bonus Badge for first task */}
                          {index === 0 && !task.is_completed && (
                            <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-[10px] font-bold px-2 py-1 rounded-full animate-pulse">
                              {style.label}
                            </div>
                          )}
                          
                          <div className="flex items-start gap-3 mb-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                              task.is_completed 
                                ? 'bg-[var(--burnz-green)]' 
                                : `bg-gradient-to-br ${style.gradient}`
                            }`}>
                              {task.is_completed ? (
                                <Check className="w-6 h-6 text-white" />
                              ) : (
                                style.icon
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-bold ${task.is_completed ? 'text-[var(--burnz-green)]' : 'text-white'}`}>
                                {task.title}
                              </p>
                              <p className={`text-lg font-bold ${task.is_completed ? 'text-[var(--burnz-green)]/70' : 'text-orange-400'}`}>
                                +{displayXP.toLocaleString()} XP
                              </p>
                            </div>
                          </div>
                          
                          {!task.is_completed && (
                            <a 
                              href={actionUrl || '#'} 
                              target={actionUrl?.startsWith('http') ? '_blank' : '_self'}
                              rel="noopener noreferrer"
                              className="w-full"
                            >
                              <Button 
                                size="sm" 
                                className={`w-full bg-gradient-to-r ${style.gradient} hover:opacity-90 text-white font-bold h-10`}
                              >
                                {index === 0 ? '🎁 Sign Up & Earn' : index === 1 ? '🔗 Connect Wallet' : '💬 Join Discord'}
                              </Button>
                            </a>
                          )}
                          
                          {task.is_completed && (
                            <div className="flex items-center justify-center gap-2 text-[var(--burnz-green)] font-bold">
                              <Check className="w-5 h-5" />
                              Completed!
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <Link to="/quests" className="inline-flex items-center gap-1 text-[var(--burnz-orange)] hover:opacity-80 text-sm mt-3">
                    View all tasks <ArrowRight className="w-4 h-4" />
                  </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Featured Hero - Partner Campaign */}
        <Card className="bg-[var(--surface-2)] border-white/5 overflow-hidden">
          <div className="relative h-64">
            <img src={DEMO.featuredGame.image} alt={DEMO.featuredGame.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
            <div className="absolute bottom-0 left-0 p-6 max-w-lg">
              <Badge className="bg-[var(--burnz-purple)] text-white text-xs border-0 mb-2">⭐ FEATURED PARTNER</Badge>
              <h2 className="text-2xl font-bold text-white mb-2">DeFi Protocol X Campaign</h2>
              <p className="text-white/60 text-sm mb-4">Complete marketing quests, earn XP & BURNZ rewards. Help spread the word!</p>
              <div className="flex items-center gap-3">
                <Button className="bg-[var(--burnz-orange)] hover:opacity-90 text-white">
                  <Rocket className="w-4 h-4 mr-2" />Start Quests
                </Button>
                <div className="flex items-center gap-2 text-white/70">
                  <Users className="w-4 h-4" />
                  <span>8,934 completions</span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: "Total XP", value: user?.xp || 2847, icon: Sparkles, color: "var(--burnz-orange)", change: "+234" },
            { label: "BURNZ", value: user?.burnz_tokens || 850, icon: Flame, color: "var(--burnz-purple)", change: "+85" },
            { label: "Rank", value: `#${user?.rank || 42}`, icon: Trophy, color: "var(--burnz-gold)", change: "↑5" },
            { label: "Streak", value: `${user?.streak_days || 7} days`, icon: Zap, color: "var(--burnz-green)", change: "🔥" },
          ].map((stat, i) => (
            <Card key={i} className="bg-[var(--surface-2)] border-white/5">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                  <span className="text-xs text-[var(--burnz-green)]">{stat.change}</span>
                </div>
                <p className="text-xl font-bold text-white">{typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}</p>
                <p className="text-xs text-white/50">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Daily Quests */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Timer className="w-5 h-5 text-orange-400" />Daily Quests
            </h2>
            <Badge variant="outline" className="border-orange-500/30 text-orange-400 text-xs">
              <Repeat className="w-3 h-3 mr-1" />Resets in 8h 24m
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {DEMO.dailyQuests.map(quest => {
              const Icon = quest.icon;
              return (
                <Card key={quest.id} className={`bg-[#1A1A1A] border-white/5 ${quest.completed ? "border-green-500/20" : "hover:border-purple-500/30"}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${quest.completed ? "bg-green-500" : "bg-white/5"}`}>
                      {quest.completed ? <Check className="w-5 h-5 text-white" /> : <Icon className="w-5 h-5 text-orange-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${quest.completed ? "text-green-400" : "text-white"}`}>{quest.title}</p>
                      <p className="text-xs text-white/30">{quest.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-orange-400">+{quest.xp_reward} XP</p>
                      {quest.streak && <p className="text-[10px] text-orange-400">🔥 {quest.streak} day streak</p>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Main Quests */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-bold text-white">Main Quests</h2>
              <div className="flex bg-[#1A1A1A] rounded-lg p-1">
                {tabs.map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === tab ? "bg-purple-500 text-white" : "text-white/50 hover:text-white/80"}`}>{tab}</button>
                ))}
              </div>
            </div>
            <Link to="/quests" className="text-purple-400 text-sm hover:text-purple-300">View All →</Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {DEMO.quests.slice(0, 4).map(quest => (
              <QuestCard key={quest.id} quest={quest} />
            ))}
          </div>
        </div>
      </div>

      {/* Right Sidebar */}
      <div className="w-72 flex-shrink-0 space-y-4">
        {/* Burn Tracker Widget */}
        <BurnTracker compact={true} />

        {/* User Level Card */}
        <Card className="bg-gradient-to-br from-purple-900/50 to-orange-900/30 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="w-12 h-12 border-2 border-purple-500">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback className="bg-purple-500">P</AvatarFallback>
              </Avatar>
              <div>
                <Badge className="bg-purple-500 text-white text-xs border-0 mb-1">{user?.level_name || "Blaze"}</Badge>
                <p className="text-white/50 text-xs">Level {user?.level || 4}</p>
              </div>
            </div>
            <div className="mb-2">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-white/50">XP Progress</span>
                <span className="text-white">{user?.xp || 2847}/{user?.next_level_xp || 4000}</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-orange-500 to-purple-500 rounded-full" style={{ width: `${user?.xp_progress || 71}%` }} />
              </div>
            </div>
            <p className="text-white/30 text-xs text-center">{100 - (user?.xp_progress || 71)}% to next level</p>
          </CardContent>
        </Card>

        {/* Fire Board */}
        <FireBoard />

        {/* Leaderboard */}
        <Card className="bg-[#1A1A1A] border-white/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Trophy className="w-4 h-4 text-yellow-400" />Top Contributors
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {DEMO.leaderboard.slice(0, 5).map((entry, i) => (
              <div key={i} className={`flex items-center gap-3 p-2 rounded-lg ${i === 0 ? "bg-yellow-500/10" : i === 1 ? "bg-gray-400/10" : i === 2 ? "bg-amber-600/10" : "bg-white/5"}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-yellow-500 text-black" : i === 1 ? "bg-gray-300 text-black" : i === 2 ? "bg-amber-600 text-white" : "bg-white/10 text-white"}`}>{entry.rank}</div>
                <span className="text-lg">{entry.avatar}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{entry.name}</p>
                  <p className="text-white/30 text-[10px]">{entry.level}</p>
                </div>
                <span className="text-orange-400 text-sm font-bold">{(entry.xp / 1000).toFixed(1)}k</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export { DashboardPage };
